<section class="edit-pet-page position-relative overflow-hidden py-4 py-lg-5">
  <div class="container position-relative z-1">

    <ngb-alert *ngIf="alert" [type]="alert.type" (closed)="closeAlert()" class="sticky-top" style="top: 10px;">
      {{ alert.message }}
    </ngb-alert>

    <div class="text-center mb-4 mb-lg-5">
      <h1 class="page-title display-5 fw-bold mb-3">Pet profile</h1>
      <app-progress-stepper
        *ngIf="showProgressStepper"
        [currentStep]="RegistrationFlowStep.PetDetails"
        [label]="'Complete your pet\'s details'">
      </app-progress-stepper>
    </div>

    <form #editPetForm="ngForm" (ngSubmit)="save()" novalidate>

      <!-- Main two column form -->
      <div class="row g-4">

        <!-- Left card -->
        <div class="col-12 col-md-6">
          <div class="form-card h-100 p-4">

            <!-- Pet name -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="petName">Pet name</label>
              <input #petName="ngModel" class="form-control field-input"
                [class.is-invalid]="petName.touched && petName.invalid"
                id="petName" name="petName"
                [(ngModel)]="pet.name"
                placeholder="Your pet's name"
                required type="text"/>
              <div *ngIf="petName.invalid && petName.errors?.required" class="field-error small">{{ errorStrings.required }}</div>
            </div>

            <!-- Species -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="petSpecies">Species</label>
              <select #petSpecies="ngModel" class="form-select field-input"
                [class.is-invalid]="petSpecies.touched && petSpecies.invalid"
                id="petSpecies" name="petSpecies"
                [(ngModel)]="selectedSpecies"
                (ngModelChange)="updateBreedsList()"
                [disabled]="loadingSpecies || pet.isTokPaymentRequired"
                required>
                <option [ngValue]="null" disabled>{{ loadingSpecies ? 'Loading...' : 'Please select...' }}</option>
                <option *ngFor="let species of speciesList" [ngValue]="species">{{ species.description }}</option>
              </select>
              <div *ngIf="petSpecies.invalid && petSpecies.errors?.required" class="field-error small">{{ errorStrings.required }}</div>
            </div>

            <!-- Breed -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="petBreed">Breed</label>
              <input #petBreed="ngModel" #petBreedTypeahead="ngbTypeahead"
                class="form-control field-input"
                [class.is-invalid]="petBreed.touched && petBreed.invalid"
                id="petBreed" name="petBreed"
                [(ngModel)]="pet.breed"
                [ngbTypeahead]="searchBreed"
                (ngModelChange)="updateSelectedBreed()"
                [disabled]="!pet.species || loadingSpecies || loadingBreeds || pet.isTokPaymentRequired"
                [placeholder]="pet.species ? 'Please enter the breed...' : 'Select species first...'"
                [editable]="false"
                placement="bottom-left"
                (focus)="focus$.next($any($event).target.value)"
                (click)="click$.next($any($event).target.value)"
                required type="text"/>
              <div *ngIf="pet.species" class="field-hint small text-muted">
                Start typing to view suggestions and select from the list.
                If you can't find it, <a href="javascript:void(0)" (click)="openBreedSelector()">click here</a> to see the full list.
              </div>
              <div *ngIf="petBreed.invalid && petBreed.errors?.required" class="field-error small">Please select a breed from the list.</div>
            </div>

            <!-- Gender -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium">Gender</label>
              <div class="d-flex gap-4 align-items-center py-1">
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input #petSex="ngModel" type="radio" class="form-check-input gender-radio mt-0"
                    name="petSex" [(ngModel)]="pet.gender" [value]="petGender.Male" [disabled]="pet.isTokPaymentRequired" required/>
                  <span class="small">Male</span>
                </label>
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input type="radio" class="form-check-input gender-radio mt-0"
                    name="petSex" [(ngModel)]="pet.gender" [value]="petGender.Female" [disabled]="pet.isTokPaymentRequired" required/>
                  <span class="small">Female</span>
                </label>
              </div>
              <div *ngIf="petSex.invalid && petSex.errors?.required" class="field-error small">{{ errorStrings.required }}</div>
            </div>

            <!-- Neutered/Spayed -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium">Neutered/Spayed</label>
              <div class="d-flex gap-4 align-items-center py-1">
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input #petNeutered="ngModel" type="radio" class="form-check-input gender-radio mt-0"
                    name="petNeutered" [(ngModel)]="pet.neutered" [value]="true" [disabled]="pet.isTokPaymentRequired" required/>
                  <span class="small">Neutered/Spayed</span>
                </label>
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input type="radio" class="form-check-input gender-radio mt-0"
                    name="petNeutered" [(ngModel)]="pet.neutered" [value]="false" [disabled]="pet.isTokPaymentRequired" required/>
                  <span class="small">Not Neutered/Spayed</span>
                </label>
              </div>
              <div *ngIf="petNeutered.invalid && petNeutered.errors?.required" class="field-error small">{{ errorStrings.required }}</div>
            </div>

            <!-- Colours/markings -->
            <div class="d-flex flex-column gap-1">
              <label class="field-label fw-medium" for="petColoursMarkings">{{ petDetailColorsFieldTitle }}</label>
              <textarea class="form-control field-textarea"
                id="petColoursMarkings" name="petColoursMarkings"
                [(ngModel)]="pet.coloursMarkings"
                [disabled]="pet.isTokPaymentRequired"
                rows="2"></textarea>
            </div>

          </div>
        </div>

        <!-- Right card -->
        <div class="col-12 col-md-6">
          <div class="form-card h-100 p-4">

            <!-- Date of birth -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="petDob">Date of birth</label>
              <div class="input-group">
                <input #petDob="ngbDatepicker" #petDobModel="ngModel"
                  class="form-control field-input"
                  [class.is-invalid]="petDobModel.touched && petDobModel.invalid"
                  name="petDob" [(ngModel)]="petDobValue"
                  placeholder="dd/mm/yyyy"
                  ngbDatepicker [maxDate]="today" [minDate]="minDate"
                  [disabled]="pet.isTokPaymentRequired"
                  required/>
                <button class="btn btn-outline-secondary calendar" (click)="petDob.toggle()" type="button">
                  <fa-icon [icon]="['fas', 'calendar-days']"></fa-icon>
                </button>
              </div>
              <div *ngIf="petDobModel.invalid && petDobModel.errors?.required" class="field-error small">{{ errorStrings.required }}</div>
              <div *ngIf="petDobModel.invalid && petDobModel.errors?.ngbDate?.invalid" class="field-error small">{{ errorStrings.invalidDateForm }}</div>
              <div *ngIf="petDobModel.invalid && petDobModel.errors?.ngbDate?.maxDate" class="field-error small">Please enter a date not later than today.</div>
            </div>

            <!-- Implant date -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="petImplantDate">Implant date</label>
              <div *ngIf="!isNewPet" class="input-group">
                <input class="form-control field-input"
                  id="petImplantDate" name="petImplantDate"
                  [ngModel]="pet.implantDate | date: 'dd/MM/y'"
                  type="text" readonly/>
              </div>
              <div *ngIf="isNewPet" class="input-group">
                <input #implantDate="ngbDatepicker" #implantDateModel="ngModel"
                  class="form-control field-input"
                  [class.is-invalid]="implantDateModel.touched && implantDateModel.invalid"
                  name="implantDate" [(ngModel)]="petImplantDateValue"
                  placeholder="dd/mm/yyyy"
                  ngbDatepicker [maxDate]="today" [minDate]="minDate"
                  [disabled]="pet.isTokPaymentRequired"
                  required/>
                <button class="btn btn-outline-secondary calendar" (click)="implantDate.toggle()" type="button">
                  <fa-icon [icon]="['fas', 'calendar-days']"></fa-icon>
                </button>
              </div>
            </div>

            <!-- Instagram -->
            <div class="d-flex flex-column gap-1 mb-3">
              <label class="field-label fw-medium" for="instagram">Pet's Instagram handle</label>
              <input #instagram="ngModel" class="form-control field-input"
                id="instagram" name="instagram"
                [(ngModel)]="pet.instagram"
                placeholder="Instagram handle"
                [disabled]="pet.isTokPaymentRequired"
                type="text"/>
            </div>

            <!-- Distinguishing features -->
            <div class="d-flex flex-column gap-1">
              <label class="field-label fw-medium" for="petDistinguishingFeatures">{{ petDetailDistinguishingFeaturesTitle }}</label>
              <textarea class="form-control field-textarea"
                id="petDistinguishingFeatures" name="petExistingConditions"
                [(ngModel)]="pet.existingConditions"
                [disabled]="pet.isTokPaymentRequired"
                rows="2"></textarea>
            </div>

          </div>
        </div>

      </div>

      <!-- Indoor/Outdoor section -->
      <div *ngIf="showIndoorOutdoorSection" class="row mt-4">
        <div class="col-12">
          <div class="form-card p-4">
            <div class="d-flex flex-column gap-1">
              <label class="field-label fw-medium">What type of cat are they?</label>
              <div class="d-flex gap-4 align-items-center py-1">
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input type="checkbox" class="form-check-input mt-0"
                    id="typeOfCatIndoor" name="typeOfCatIndoor"
                    [(ngModel)]="checkedIsIndoor" [checked]="checkedIsIndoor"
                    (change)="toggleOutdoor(false)"
                    [required]="checkedIsIndoor || !checkedIsOutdoor"/>
                  <span class="small">Strictly indoor</span>
                </label>
                <label class="d-flex align-items-center gap-2 cursor-pointer mb-0">
                  <input type="checkbox" class="form-check-input mt-0"
                    id="typeOfCatOutdoor" name="typeOfCatOutdoor"
                    [(ngModel)]="checkedIsOutdoor" [checked]="checkedIsOutdoor"
                    (change)="toggleOutdoor(true)"
                    [required]="!checkedIsIndoor || checkedIsOutdoor"/>
                  <span class="small">Outdoor</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="d-flex flex-column align-items-center gap-3 mt-4">
        <button *ngIf="isConfirm && canAddAnotherPet && userRegistrationIsInProgress"
          type="submit"
          class="btn btn-action-secondary fw-semibold"
          [disabled]="!editPetForm.valid || loading"
          (click)="onSaveAndAddAnotherButtonClick()">
          <fa-icon *ngIf="loading" [icon]="['fas', 'spinner']" [animation]="'spin'" class="me-2"></fa-icon>
          {{ loading ? 'Saving' : 'Add another pet' }}
        </button>
        <button type="submit"
          class="btn btn-action fw-semibold"
          [disabled]="!editPetForm.valid || loading">
          <fa-icon *ngIf="loading" [icon]="['fas', 'spinner']" [animation]="'spin'" class="me-2"></fa-icon>
          {{ isConfirm ? (loading ? 'Saving' : 'Next') : (loading ? 'Saving' : 'Save') }}
        </button>
      </div>

    </form>
  </div>

  <app-background-icon
    src="/assets/images/icons/treats-pink.svg"
    [top]="20"
    [left]="15"
    [width]="175"
    [opacity]="0.5"
  />
  <app-background-icon
    src="/assets/images/icons/carry_crate-pink.svg"
    [top]="20"
    [left]="60"
    [width]="230"
    [opacity]="0.5"
  />
  <app-background-icon
    src="/assets/images/icons/cat-pink.svg"
    [top]="20"
    [left]="90"
    [width]="300"
    [opacity]="0.5"
  />
  <app-background-icon
    src="/assets/images/icons/kennel-pink.svg"
    [top]="60"
    [left]="5"
    [width]="140"
    [opacity]="0.5"
  />
  <app-background-icon
    src="/assets/images/icons/medicine-pink.svg"
    [top]="95"
    [left]="20"
    [width]="210"
    [opacity]="0.5"
  />
  <app-background-icon
    src="/assets/images/icons/paw-pink.svg"
    [top]="80"
    [left]="95"
    [width]="130"
    [opacity]="0.5"
  />

</section>
